import { useState, useEffect } from 'react';
import { Users, FileCheck, FileX, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import api from '../api/axios';
import PageHeader from '../components/layout/PageHeader';
import StatsCard from '../components/dashboard/StatsCard';
import StatusChart from '../components/dashboard/StatusChart';
import ACBreakdownTable from '../components/dashboard/ACBreakdownTable';
import { formatDate } from '../utils/formatDate';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/stats')
      .then(res => setStats(res.data))
      .catch(err => console.error('Failed to load stats:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Overview of registration data" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatsCard title="Total Registrations" value={stats?.total} icon={Users} color="teal" />
        <StatsCard title="Submitted" value={stats?.statusCounts?.SUBMITTED} icon={Clock} color="blue" />
        <StatsCard title="Approved" value={stats?.statusCounts?.APPROVED} icon={FileCheck} color="green" />
        <StatsCard title="Rejected" value={stats?.statusCounts?.REJECTED} icon={FileX} color="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <StatusChart statusCounts={stats?.statusCounts} />
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Top Mandals</h3>
          {stats?.topMandals?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.topMandals} layout="vertical" margin={{ left: 80 }}>
                <XAxis type="number" />
                <YAxis type="category" dataKey="mandal" tick={{ fontSize: 12 }} width={80} />
                <Tooltip />
                <Bar dataKey="count" fill="#14b8a6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-400 text-center py-10">No data</div>
          )}
        </div>
      </div>

      <ACBreakdownTable data={stats?.acBreakdown} />

      {stats?.recentImports?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mt-4">
          <h3 className="font-semibold text-gray-800 mb-4">Recent Imports</h3>
          <div className="space-y-2">
            {stats.recentImports.map(imp => (
              <div key={imp.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-100 last:border-0">
                <div>
                  <span className="font-medium">{imp.file_name}</span>
                  <span className="text-gray-500 ml-2">({imp.success_count} records)</span>
                </div>
                <div className="text-gray-500 text-xs">{formatDate(imp.imported_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
